import axios from 'axios';
import { offlineManager } from './OfflineManager';

// Create axios instance
const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || '',
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add the auth token to headers
api.interceptors.request.use(
    async (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        
        // Offline handling
        if (!navigator.onLine) {
            // For GET requests, we can try to serve from cache
            if (config.method === 'get') {
                const cachedData = await offlineManager.getCachedResponse(config.url);
                if (cachedData) {
                    // Throw a special error that carries the cached data
                    // This will be caught by the response error interceptor
                    const error = new Error('Offline - Cached');
                    error.cachedData = cachedData;
                    error.isCached = true;
                    throw error;
                }
            } 
            // For mutation requests (POST, PUT, DELETE, PATCH), queue them
            else if (['post', 'put', 'delete', 'patch'].includes(config.method)) {
                await offlineManager.queueRequest({
                    url: config.url,
                    method: config.method,
                    data: config.data,
                    params: config.params,
                    headers: config.headers
                });
                
                // Throw a special error to indicate the request was queued
                const error = new Error('Offline - Queued');
                error.isQueued = true;
                throw error;
            }
        }
        
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle token expiration and caching
api.interceptors.response.use(
    (response) => {
        // Cache successful GET requests
        if (response.config.method === 'get' && response.status === 200) {
            offlineManager.cacheResponse(response.config.url, response.data);
        }
        return response;
    },
    async (error) => {
        // Handle Offline Scenarios
        if (error.isCached) {
            // Return cached data as if it were a successful response
            return Promise.resolve({
                data: error.cachedData,
                status: 200,
                statusText: 'OK',
                headers: {},
                config: error.config || {},
                isCached: true
            });
        }

        if (error.isQueued) {
            // Return a success-like response for queued actions
            // This enables optimistic UI updates
            return Promise.resolve({
                data: { success: true, message: 'Action queued for offline sync' },
                status: 202,
                statusText: 'Accepted',
                headers: {},
                config: error.config || {},
                isQueued: true
            });
        }

        // Navigator says "online" but the request never reached a server —
        // dead valley, captive portal, timed-out data. That's the REAL
        // offline case on a phone: queue the mutation instead of letting
        // it silently fail. (Previously only navigator.onLine=false queued,
        // which phones almost never report.)
        const isNetworkError = !error.response && (
            error.code === 'ERR_NETWORK' ||
            error.code === 'ECONNABORTED' ||
            /network error|timeout/i.test(error.message || '')
        );
        if (isNetworkError && error.config) {
            const cfg = error.config;
            const isAuthEndpoint = /\/auth\/(login|register|forgot|reset)/.test(cfg.url || '');
            if (!isAuthEndpoint && ['post', 'put', 'delete', 'patch'].includes(cfg.method)) {
                try {
                    await offlineManager.queueRequest({
                        url: cfg.url,
                        method: cfg.method,
                        data: cfg.data,
                        params: cfg.params,
                        headers: cfg.headers,
                    });
                    return Promise.resolve({
                        data: { success: true, message: 'Saved offline — will sync when back in coverage.' },
                        status: 202,
                        statusText: 'Accepted',
                        headers: {},
                        config: cfg,
                        isQueued: true,
                    });
                } catch (queueErr) {
                    console.error('Offline queue write failed:', queueErr);
                }
            }
            // GET while network-error — try the cached copy too (flaky
            // "online" shouldn't hard-fail list views either)
            if (cfg.method === 'get') {
                const cachedData = await offlineManager.getCachedResponse(cfg.url);
                if (cachedData) {
                    return Promise.resolve({
                        data: cachedData,
                        status: 200,
                        statusText: 'OK',
                        headers: {},
                        config: cfg,
                        isCached: true,
                    });
                }
            }
        }

        if (error.response && error.response.status === 401) {
            // Don't nuke a good session over one flaky 401 — a background
            // poll hitting a stale/permissioned endpoint was logging the
            // worker out mid-run. Only clear when the JWT is actually
            // expired or undecodable.
            const token = localStorage.getItem('token');
            let expired = true;
            try {
                const { exp } = JSON.parse(atob(token.split('.')[1]));
                expired = exp * 1000 < Date.now();
            } catch { expired = true; }
            if (expired) {
                localStorage.removeItem('token');
                const publicPaths = ['/login', '/signup', '/forgot-password', '/reset-password'];
                if (!publicPaths.some(path => window.location.pathname.startsWith(path))) {
                    window.location.href = '/login';
                }
            }
        }
        return Promise.reject(error);
    }
);

export default api;
